import React, { useEffect, useRef, useState } from 'react';

// Bespoke cursor: a minimalist FlexiOss bone-scaffold block that floats toward
// the pointer and pulses over interactive elements. Disabled on touch devices.
const INTERACTIVE = 'a, button, [role="button"], input, select, textarea, label, .cursor-pointer';

const BoneCursor = () => {
  const [enabled] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );
  const dotRef = useRef(null);
  const target = useRef({ x: -100, y: -100 });
  const pos = useRef({ x: -100, y: -100 });
  const hovering = useRef(false);
  const raf = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    document.body.classList.add('bone-cursor-active');

    const onMove = (e) => {
      target.current = { x: e.clientX, y: e.clientY };
      const interactive = e.target.closest && e.target.closest(INTERACTIVE);
      const node = dotRef.current;
      if (node) node.classList.toggle('bone-cursor--hover', !!interactive);
    };
    const onLeave = (e) => {
      // Only hide when the pointer actually leaves the window — NOT on element-to-element
      // mouseout (which fires constantly under the pointer while scrolling and caused the
      // cursor to jump to the upper-left corner).
      if (!e.relatedTarget) { target.current = { x: -100, y: -100 }; }
    };

    const loop = () => {
      pos.current.x += (target.current.x - pos.current.x) * 0.5;
      pos.current.y += (target.current.y - pos.current.y) * 0.5;
      const node = dotRef.current;
      if (node) node.style.transform = `translate3d(${pos.current.x}px, ${pos.current.y}px, 0) translate(-50%, -50%)`;
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);

    window.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseout', onLeave);
    return () => {
      document.body.classList.remove('bone-cursor-active');
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseout', onLeave);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <div ref={dotRef} className="bone-cursor" data-testid="bone-cursor" aria-hidden="true">
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
        {/* Long-bone glyph — knobbed ends + diagonal shaft */}
        <g stroke="#60a5fa" strokeWidth="1.5" strokeLinecap="round" fill="none">
          <circle cx="8" cy="11.5" r="3.1" />
          <circle cx="11.5" cy="8" r="3.1" />
          <circle cx="28" cy="24.5" r="3.1" />
          <circle cx="24.5" cy="28" r="3.1" />
          <path d="M11 11 L25 25" />
        </g>
        {/* Regeneration core — pulses as if new bone is forming */}
        <circle cx="18" cy="18" r="2" fill="#E87A20">
          <animate attributeName="r" values="1.4;3;1.4" dur="1.7s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="1;0.55;1" dur="1.7s" repeatCount="indefinite" />
        </circle>
        {/* New-bone particles bridging the gap */}
        <circle cx="14" cy="15" r="0.9" fill="#fbbf24">
          <animate attributeName="opacity" values="0.15;1;0.15" dur="1.7s" begin="0.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="22" cy="21" r="0.9" fill="#fbbf24">
          <animate attributeName="opacity" values="0.15;1;0.15" dur="1.7s" begin="0.85s" repeatCount="indefinite" />
        </circle>
      </svg>
    </div>
  );
};

export default BoneCursor;
